// These legacy names are persistent browser storage identifiers, not product branding.
const LOCAL_ASSET_CACHE = "jftt-local-assets-v1";
const LOCAL_ASSET_DATABASE = "jftt-local-assets";
const LOCAL_ASSET_STORE = "files";
let databasePromise: Promise<IDBDatabase> | undefined;

const cachedAssetRequest = (path: string) => new Request(new URL(`/__jftt_local_asset__/${encodeURIComponent(path)}`, location.origin));

function fallbackDatabase() {
  databasePromise ??= new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(LOCAL_ASSET_DATABASE, 1);
    request.onupgradeneeded = () => { if (!request.result.objectStoreNames.contains(LOCAL_ASSET_STORE)) request.result.createObjectStore(LOCAL_ASSET_STORE); };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Could not open local asset storage."));
  });
  return databasePromise;
}

async function saveIndexedAsset(path: string, source: Blob) {
  const database = await fallbackDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(LOCAL_ASSET_STORE, "readwrite");
    transaction.objectStore(LOCAL_ASSET_STORE).put(source, path);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error("Could not save the local asset."));
    transaction.onabort = () => reject(transaction.error ?? new Error("Saving the local asset was cancelled."));
  });
}

async function readIndexedAsset(path: string) {
  const database = await fallbackDatabase();
  return new Promise<Blob | null>((resolve, reject) => {
    const request = database.transaction(LOCAL_ASSET_STORE, "readonly").objectStore(LOCAL_ASSET_STORE).get(path);
    request.onsuccess = () => resolve(request.result instanceof Blob ? request.result : null);
    request.onerror = () => reject(request.error ?? new Error("Could not read the local asset."));
  });
}

async function deleteIndexedAsset(path: string) {
  const database = await fallbackDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(LOCAL_ASSET_STORE, "readwrite");
    transaction.objectStore(LOCAL_ASSET_STORE).delete(path);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error("Could not delete the local asset."));
  });
}

export async function saveLocalAssetFallback(path: string, source: Blob) {
  try {
    if (!("caches" in globalThis)) throw new Error("Cache Storage is unavailable.");
    await (await caches.open(LOCAL_ASSET_CACHE)).put(cachedAssetRequest(path), new Response(source, { headers: { "Content-Type": source.type || "application/octet-stream" } }));
  } catch { await saveIndexedAsset(path, source); }
}

export async function readLocalAssetFallback(path: string): Promise<File | null> {
  try {
    if ("caches" in globalThis) {
      const response = await (await caches.open(LOCAL_ASSET_CACHE)).match(cachedAssetRequest(path));
      if (response) {
        const blob = await response.blob();
        return new File([blob], path.split("/").at(-1) ?? "asset", { type: blob.type, lastModified: Date.now() });
      }
    }
  } catch { /* try IndexedDB */ }
  try {
    const blob = await readIndexedAsset(path); if (!blob) return null;
    return new File([blob], path.split("/").at(-1) ?? "asset", { type: blob.type, lastModified: Date.now() });
  } catch { return null; }
}

export async function deleteLocalAssetFallback(path: string) {
  try { if ("caches" in globalThis) await (await caches.open(LOCAL_ASSET_CACHE)).delete(cachedAssetRequest(path)); }
  catch { /* already absent */ }
  try { await deleteIndexedAsset(path); }
  catch { /* already absent */ }
}
